import { Product } from '@/types';
import { useEffect, useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';

type FormUserProps = {
  listProducts: Product[];
}

interface FormDataList {
    name: string,
    email: string,
    phone: string,
    note: string,
    product_list?: Product[],
}

const defaultFormData: FormDataList = {
    name: '',
    email: '',
    phone: '',
    note: '',
    product_list: [],   
}

export const FormUser = ({ listProducts }: FormUserProps) => {
    const [formData, setFormData] = useState(defaultFormData);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [sendList, setSendList] = useState(false);

    useEffect(() => {
        if (listProducts.length > 0) {
            setSendList(true);

            setFormData(prev => ({
                ...prev,
                product_list: listProducts,
            }))
        }
    }, [listProducts])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.currentTarget;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateForm = (): boolean => {
        if (!formData.name.trim()) {
            setError('Imię jest wymagane');
            return false;
        }
        if (!formData.email.trim()) {
            setError('Email jest wymagany');
            return false;
        }
        if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            setError('Nieprawidłowy format email');
            return false;
        }
        if (!formData.phone.trim()) {
            setError('Nr telefonu jest wymagany');
            return false;
        }
        if (!formData.phone.match(/^\d{9}$/)) {
            setError('Nr telefonu musi zawierać 9 cyfr');
            return false;
        }
        setError('');
        return true;
    };

    const sendEmail = async (endpoint: string) => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setMessage('');

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (!response.ok) {
                setError(data.error || 'Błąd wysyłania wiadomości');
                return;
            }

            setMessage(data.message);
            // Clear form after successful send
            setFormData({ name: '', email: '', phone: '', note: '', product_list: [] });
            
            // Clear message after 5 seconds
            setTimeout(() => setMessage(''), 5000);
        } catch (err) {
            setError('Błąd połączenia z serwerem');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const getCsrfToken = (): string => {
        const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        return token || '';
    };

    return (
        <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            {error && (
                <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                    {error}
                </div>
            )}

            {message && (
                <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
                    {message}
                </div>
            )}
            
            <div className="mb-4">
                <Label className="m-4 cursor-pointer">
                <Checkbox
                    name="sendList"
                    checked={sendList}
                    onCheckedChange={() => {
                        setSendList(!sendList);
                        setFormData(prev => ({
                            ...prev,
                            product_list: !sendList ? products : []
                        }));
                    }}
                    className='cursor-pointer'
                />
                Dołącz liste do formularza
                </Label>
            </div>

            <div className="mb-4">
                <Input
                    type="text"
                    name="name"
                    placeholder="Imię"
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={loading}
                    className="h-12 pl-4 pr-4 text-base m-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-600 disabled:bg-gray-100"
                />
            </div>

            <div className="mb-4">
                <Input
                    type="email"
                    name="email"
                    placeholder="E-mail"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={loading}
                    className="h-12 pl-4 pr-4 text-base m-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-600 disabled:bg-gray-100"
                />
            </div>

            <div className="mb-4">
                <Input
                    type="tel"
                    name="phone"
                    placeholder="Nr telefonu"
                    value={formData.phone}
                    onChange={handleInputChange}
                    disabled={loading}
                    className="h-12 pl-4 pr-4 text-base m-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-600 disabled:bg-gray-100"
                />
            </div>

            <div className="mb-4">
                <Textarea
                    name="note"
                    placeholder="Notatka (opcjonalnie)"
                    value={formData.note}
                    onChange={handleInputChange}
                    disabled={loading}
                    className="h-40 w-full px-4 py-2 text-base m-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-600 disabled:bg-gray-100 resize-none"
                />
            </div>

            <div className="flex gap-4 justify-end p-4">
                <Button
                    onClick={() => sendEmail('/forms/send_form_list')}
                    disabled={loading}
                    className="h-12 px-4 py-2 bg-green-600 text-white font-semibold cursor-pointer rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
                >
                    {loading ? 'Wysyłanie...' : 'Wyślij'}
                </Button>
            </div>
        </section>
    );
}